import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { BankAccountFormDTO } from '../types/BankAccount';
import CustomInput from '../controls/CustomInput';
import { FormSelect } from '../controls/FormSelect';

const BankAccountFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  const [formData, setFormData] = useState<BankAccountFormDTO>({
    id: 0,
    glCode: '',
    assetsType: '',
    debitCredit: '',
    accountType: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadBankAccount = async (accountId: number) => {
      try {
        setIsLoading(true);
        const response = await api.get<{ bankAccount: BankAccountFormDTO }>(
          `/api/bankaccount/getbankaccount?id=${accountId}`
        );
        setFormData(response.data.bankAccount);
      } catch (error) {
        console.error('Error loading bank account:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const accountId = new URLSearchParams(location.search).get('bankAccountId');
    if (accountId) {
      setIsEditing(true);
      loadBankAccount(parseInt(accountId));
    }
  }, [location.search, api]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!familyId) return;

    try {
      setIsLoading(true);

      if (isEditing) {
        await api.put(`/api/bankaccount/updatebankaccount`, formData);
      } else {
        await api.post(`/api/bankaccount/createbankaccount?familyId=${familyId}`, formData);
      }

      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving bank account:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Bank Account' : 'Add New Bank Account'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update bank account information' : 'Enter bank account information to add to the family'}
              </p>
            </div>
          </div>

          {/* Bank Account Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Bank Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CustomInput
                label="GL Code"
                name="glCode"
                value={formData.glCode}
                onChange={(val) => setFormData(prev => ({ ...prev, glCode: val }))}
                required
              />

              <CustomInput
                label="Assets Type"
                name="assetsType"
                value={formData.assetsType}
                onChange={(val) => setFormData(prev => ({ ...prev, assetsType: val }))}
                required
              />

              <FormSelect
                label="Debit / Credit"
                value={formData.debitCredit || null}
                options={[
                  { value: 'Debit', label: 'Debit' },
                  { value: 'Credit', label: 'Credit' },
                ]}
                onChange={(val) => setFormData(prev => ({ ...prev, debitCredit: (val as string) ?? '' }))}
                required
              />

              <CustomInput
                label="Account Type"
                name="accountType"
                value={formData.accountType}
                onChange={(val) => setFormData(prev => ({ ...prev, accountType: val }))}
                required
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Bank Account' : 'Add Bank Account')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BankAccountFormPage;
